import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";
import ColorInit from "../helper/ColorInit";

const Blog = () => {
    const navigate = useNavigate();
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState({});

    useEffect(() => {
        fetchBlogPosts();
    }, []);

    const fetchBlogPosts = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/blog/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch blog posts: ${response.status}`);
            }

            const data = await response.json();
            setBlogPosts(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handlePostClick = (post) => {
        setSelectedPost(post);
        setCurrentSlideIndex({ [post.id]: 0 });
    };

    const handleCloseModal = () => {
        setSelectedPost(null);
    };

    const handleNextSlide = (postId, totalSlides) => {
        setCurrentSlideIndex(prev => ({
            ...prev,
            [postId]: (prev[postId] + 1) % totalSlides
        }));
    };

    const handlePrevSlide = (postId, totalSlides) => {
        setCurrentSlideIndex(prev => ({
            ...prev,
            [postId]: prev[postId] === 0 ? totalSlides - 1 : prev[postId] - 1
        }));
    };

    const handleContactRedirect = () => {
        navigate('/contact');
        handleCloseModal();
    };

    const getCategoryColor = (category) => {
        const colors = {
            'News': '#8A7F8D',      // Purple-gray
            'Tips': '#7A6F8B',      // Darker purple-gray
            'Updates': '#9D9DC0',   // Soft blue-gray
            'Offers': '#D4A5C4',    // Soft pink
            'Events': '#A5C4A5',    // Soft green
            'Default': '#8A7F8D'    // Default
        };
        return colors[category] || colors.Default;
    };

    const heroStyles = {
        background: "linear-gradient(135deg, #1D2A3A 0%, #2C3E50 100%)",
        color: "white",
        padding: "80px 20px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        marginBottom: "40px"
    };

    const titleStyles = {
        fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
        fontWeight: "800",
        marginBottom: "20px",
        color: "white",
        textShadow: "0 2px 10px rgba(0,0,0,0.1)"
    };

    if (loading) {
        return (
            <>
                <HeaderOne />
                <ColorInit color={true} />
                <div className="blog-container" style={{ backgroundColor: "#FAF9F6", minHeight: "100vh" }}>
                    <section className="blog-hero-section" style={heroStyles}>
                        <div className="hero-content" style={{ maxWidth: "800px", margin: "0 auto" }}>
                            <h1 className="hero-title" style={titleStyles}>What's New at Panda Print</h1>
                            <p style={{ color: "white", opacity: 0.9 }}>Loading blog posts...</p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error) {
        return (
            <>
                <HeaderOne />
                <ColorInit color={true} />
                <div className="blog-container" style={{ backgroundColor: "#FAF9F6", minHeight: "100vh" }}>
                    <section className="blog-hero-section" style={heroStyles}>
                        <div className="hero-content" style={{ maxWidth: "800px", margin: "0 auto" }}>
                            <h1 className="hero-title" style={titleStyles}>What's New at Panda Print</h1>
                            <p style={{ color: "white", opacity: 0.9 }}>Error loading blog posts. Please try again later.</p>
                        </div>
                    </section>
                </div>
                <BottomFooter />
            </>
        );
    }

    return (
        <>
            <HeaderOne />
            <ColorInit color={true} />

            <div className="blog-container" style={{ backgroundColor: "#FAF9F6", minHeight: "100vh" }}>
                {/* Hero Section */}
                <section className="blog-hero-section" style={heroStyles}>
                    <div className="hero-glow" style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-20%",
                        width: "140%",
                        height: "200%",
                        background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                        pointerEvents: "none"
                    }}></div>
                    <div className="hero-content" style={{ maxWidth: "1000px", margin: "0 auto", position: "relative", zIndex: 1 }}>
                        <h1 className="hero-title" style={titleStyles}>
                            What's New at Panda Print
                        </h1>
                        <p style={{ fontSize: "1.25rem", opacity: "0.95", lineHeight: "1.6", color: "white", maxWidth: "700px", margin: "0 auto" }}>
                            Check here for the latest news, updates, and special offers
                        </p>
                    </div>
                </section>

                {/* Main Content */}
                <div className="blog-main-content" style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px 80px 20px" }}>
                    {/* Section Title */}
                    <div style={{ marginBottom: "40px" }}>
                        <h2 style={{
                            color: "#7A6F8B",
                            fontSize: "2.2rem",
                            marginBottom: "10px",
                            paddingBottom: "10px",
                            borderBottom: "3px solid #D4E2D4",
                            fontWeight: "300",
                            letterSpacing: "1px"
                        }}>
                            Latest Articles
                        </h2>
                    </div>

                    {/* Blog Grid */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
                        gap: "30px"
                    }}>
                        {blogPosts.map((post) => (
                            <div
                                key={post.id}
                                className="blog-card"
                                onClick={() => handlePostClick(post)}
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "16px",
                                    border: "2px solid #E0DBE6",
                                    boxShadow: "0 4px 20px rgba(138, 127, 141, 0.06)",
                                    borderTop: `4px solid ${getCategoryColor(post.category)}`,
                                    overflow: "hidden",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column"
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 8px 30px rgba(138, 127, 141, 0.15)";
                                    e.currentTarget.style.borderColor = getCategoryColor(post.category);
                                    e.currentTarget.style.transform = "translateY(-5px)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(138, 127, 141, 0.06)";
                                    e.currentTarget.style.borderColor = "#E0DBE6";
                                    e.currentTarget.style.transform = "translateY(0)";
                                }}
                            >
                                {/* card image */}
                                <div style={{ height: "220px", overflow: "hidden", position: "relative" }}>
                                    <img
                                        src={post.images[0]?.image_url || 'https://via.placeholder.com/600x400?text=No+Image'}
                                        alt={post.images[0]?.alt_text || post.title}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                    <div style={{
                                        position: "absolute",
                                        top: "15px",
                                        left: "15px",
                                        backgroundColor: getCategoryColor(post.category),
                                        color: "white",
                                        padding: "4px 12px",
                                        borderRadius: "20px",
                                        fontSize: "12px",
                                        fontWeight: "600"
                                    }}>
                                        {post.category}
                                    </div>
                                </div>

                                {/* card content */}
                                <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                                    <div style={{ color: "#9D9DC0", fontSize: "0.85rem", marginBottom: "8px" }}>
                                        📅 {post.formatted_date}
                                    </div>
                                    <h3 style={{
                                        color: "#7A6F8B",
                                        margin: "0 0 12px 0",
                                        fontSize: "1.3rem",
                                        fontWeight: "600",
                                        lineHeight: "1.4"
                                    }}>
                                        {post.title}
                                    </h3>
                                    <p style={{
                                        color: "#8A7F8D",
                                        fontSize: "0.95rem",
                                        lineHeight: "1.6",
                                        margin: "0 0 20px 0",
                                        display: "-webkit-box",
                                        WebkitLineClamp: "3",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden"
                                    }}>
                                        {post.excerpt}
                                    </p>
                                    <div style={{ marginTop: "auto", color: getCategoryColor(post.category), fontWeight: "600", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "5px" }}>
                                        Read More <span style={{ fontSize: "1.2rem" }}>→</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Modal for Full Post */}
            {selectedPost && (
                <div
                    className="blog-modal-overlay"
                    onClick={handleCloseModal}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        padding: "20px"
                    }}
                >
                    <div
                        className="blog-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: "16px",
                            maxWidth: "1000px",
                            width: "100%",
                            maxHeight: "90vh",
                            overflow: "auto",
                            position: "relative"
                        }}
                    >
                        <button
                            className="modal-close"
                            onClick={handleCloseModal}
                            style={{
                                position: "absolute",
                                top: "20px",
                                right: "20px",
                                backgroundColor: "#EFD1D4",
                                color: "gray",
                                border: "none",
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                cursor: "pointer",
                                zIndex: 1001,
                                fontSize: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)"
                            }}
                        >
                            ✕
                        </button>

                        {/* Image Slider */}
                        {selectedPost.images && selectedPost.images.length > 0 && (
                            <div style={{ position: "relative" }}>
                                <div style={{
                                    height: "500px",
                                    overflow: "hidden",
                                    borderTopLeftRadius: "16px",
                                    borderTopRightRadius: "16px"
                                }}>
                                    <img
                                        src={selectedPost.images[currentSlideIndex[selectedPost.id] || 0]?.image_url}
                                        alt={selectedPost.images[currentSlideIndex[selectedPost.id] || 0]?.alt_text || "Blog image"}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                </div>

                                {selectedPost.images.length > 1 && (
                                    <>
                                        <button
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "20px",
                                                transform: "translateY(-50%)",
                                                backgroundColor: "#EFD1D4",
                                                color: "gray",
                                                border: "none",
                                                width: "50px",
                                                height: "50px",
                                                borderRadius: "50%",
                                                cursor: "pointer",
                                                fontSize: "24px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)"
                                            }}
                                            onClick={() => handlePrevSlide(selectedPost.id, selectedPost.images.length)}
                                        >
                                            ❮
                                        </button>
                                        <button
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                right: "20px",
                                                transform: "translateY(-50%)",
                                                backgroundColor: "#EFD1D4",
                                                color: "gray",
                                                border: "none",
                                                width: "50px",
                                                height: "50px",
                                                borderRadius: "50%",
                                                cursor: "pointer",
                                                fontSize: "24px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)"
                                            }}
                                            onClick={() => handleNextSlide(selectedPost.id, selectedPost.images.length)}
                                        >
                                            ❯
                                        </button>
                                        <div style={{
                                            position: "absolute",
                                            bottom: "20px",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            display: "flex",
                                            gap: "10px"
                                        }}>
                                            {selectedPost.images.map((_, idx) => (
                                                <span
                                                    key={idx}
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        backgroundColor: idx === (currentSlideIndex[selectedPost.id] || 0) ? "#8A7F8D" : "rgba(138, 127, 141, 0.5)",
                                                        cursor: "pointer",
                                                        transition: "background-color 0.3s"
                                                    }}
                                                    onClick={() => setCurrentSlideIndex(prev => ({ ...prev, [selectedPost.id]: idx }))}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Modal Content */}
                        <div style={{ padding: "40px" }}>
                            <span
                                style={{
                                    backgroundColor: getCategoryColor(selectedPost.category),
                                    color: "white",
                                    padding: "6px 15px",
                                    borderRadius: "20px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "inline-block",
                                    marginBottom: "20px"
                                }}
                            >
                                {selectedPost.category}
                            </span>
                            <h2 style={{ color: "#7A6F8B", fontSize: "2rem", margin: "0 0 10px 0", fontWeight: "600" }}>
                                {selectedPost.title}
                            </h2>
                            <p style={{ color: "#9D9DC0", margin: "0 0 30px 0", fontSize: "1rem" }}>
                                Published on {selectedPost.formatted_date}
                            </p>
                            <div style={{ color: "#7A6F8B", lineHeight: "1.8", fontSize: "1.1rem", marginBottom: "40px" }}>
                                <p>{selectedPost.full_content}</p>
                            </div>
                            <button
                                style={{
                                    backgroundColor: "#EFD1D4",
                                    color: "gray",
                                    border: "none",
                                    padding: "15px 40px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                    transition: "all 0.3s ease",
                                    display: "block",
                                    margin: "0 auto"
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#E8C1C5"}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#EFD1D4"}
                                onClick={handleContactRedirect}
                            >
                                Contact Us for More Details
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <BottomFooter />
        </>
    );
};

export default Blog;